import { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Button, Stack, Typography, Alert, Autocomplete, TextField, CircularProgress } from '@mui/material';
import { MyLocation } from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { locationService } from '@smartgrow/shared';
import type { GeocodeResult } from '@smartgrow/shared';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const NIGERIA_CENTER: [number, number] = [9.082, 8.6753];

interface LocationPickerProps {
  latitude?: number;
  longitude?: number;
  onChange: (lat: number, lng: number) => void;
}

const ClickHandler = ({ onChange }: { onChange: (lat: number, lng: number) => void }) => {
  useMapEvents({
    click: (e) => onChange(Number(e.latlng.lat.toFixed(6)), Number(e.latlng.lng.toFixed(6))),
  });
  return null;
};

// MapContainer's center prop only sets the initial view — recenter explicitly whenever the
// pin moves afterward (e.g. from the "Use my location" button), since the map stays mounted.
const RecenterOnChange = ({ lat, lng }: { lat: number; lng: number }) => {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lng], map.getZoom() < 10 ? 12 : map.getZoom());
  }, [lat, lng, map]);
  return null;
};

export const LocationPicker = ({ latitude, longitude, onChange }: LocationPickerProps) => {
  const [geoError, setGeoError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchOptions, setSearchOptions] = useState<GeocodeResult[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasPin = latitude != null && longitude != null;
  const center: [number, number] = useMemo(
    () => (hasPin ? [latitude!, longitude!] : NIGERIA_CENTER),
    [hasPin, latitude, longitude],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query = searchInput.trim();
    if (query.length < 3) { setSearchOptions([]); return; }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      locationService.search(query)
        .then((results) => setSearchOptions(results))
        .catch(() => setSearchOptions([]))
        .finally(() => setSearching(false));
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchInput]);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by this browser.');
      return;
    }
    setLocating(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange(Number(pos.coords.latitude.toFixed(6)), Number(pos.coords.longitude.toFixed(6)));
        setLocating(false);
      },
      (err) => {
        setGeoError(err.code === err.PERMISSION_DENIED
          ? 'Location permission denied. Search a place name instead, or click the map.'
          : 'Could not get your location. Search a place name instead, or click the map.');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
        <Autocomplete
          fullWidth
          size="small"
          filterOptions={(x) => x}
          options={searchOptions}
          getOptionLabel={(o) => o.displayName}
          isOptionEqualToValue={(o, v) => o.displayName === v.displayName}
          loading={searching}
          noOptionsText={searchInput.trim().length < 3 ? 'Type at least 3 characters' : 'No matches found'}
          onInputChange={(_, value) => setSearchInput(value)}
          onChange={(_, value) => { if (value) onChange(value.latitude, value.longitude); }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search for a place"
              placeholder="e.g. Kaduna, Nigeria"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searching ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button size="small" sx={{ whiteSpace: 'nowrap', mt: 0.5 }} startIcon={<MyLocation />} onClick={handleUseMyLocation} disabled={locating}>
          {locating ? 'Locating…' : 'Use my location'}
        </Button>
      </Stack>
      {geoError && <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setGeoError(null)}>{geoError}</Alert>}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Or fine-tune by clicking the map:
      </Typography>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <MapContainer center={center} zoom={hasPin ? 12 : 6} style={{ height: 260, width: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <ClickHandler onChange={onChange} />
          {hasPin && <RecenterOnChange lat={center[0]} lng={center[1]} />}
          {hasPin && <Marker position={center} />}
        </MapContainer>
      </Box>
    </Box>
  );
};
